/**
 * Performance Timing Service
 * 
 * Centralized service for recording performance marks and measuring durations
 * using the browser Performance API.
 * 
 * Responsibilities:
 * - Record performance marks at key application lifecycle events
 * - Measure durations between marks
 * - Provide read-only APIs for diagnostics and testing
 * - Support React StrictMode (idempotent marks)
 * 
 * Clean Architecture: This is a boundary/infrastructure service - framework-agnostic,
 * no React imports, and decoupled from UI logic.
 */

import { Logger } from '@/app/utils/logger';

const LOG_TAG = 'performance-timing';

/**
 * Performance mark data
 */
export interface PerformanceMark {
  name: string;
  timestamp: number;
}

/**
 * Performance duration measurement
 */
export interface PerformanceDuration {
  name: string;
  startMark: string;
  endMark: string;
  duration: number;
}

/**
 * Well-known performance mark names
 */
export const PERFORMANCE_MARKS = {
  APP_BOOTSTRAP_START: 'app:bootstrap:start',
  APP_BOOTSTRAP_END: 'app:bootstrap:end',
  PLAYLIST_READY: 'playlist:ready',
  ATC_STATIONS_READY: 'atc:stations:ready',
  PLAYBACK_FIRST_START: 'playback:first:start',
} as const;

/**
 * Well-known duration names
 */
export const PERFORMANCE_DURATIONS = {
  BOOTSTRAP: 'duration:bootstrap',
  TIME_TO_PLAYLIST_READY: 'duration:time-to-playlist-ready',
  TIME_TO_ATC_READY: 'duration:time-to-atc-ready',
  TIME_TO_FIRST_PLAYBACK: 'duration:time-to-first-playback',
} as const;

/**
 * Performance Timing Service - Singleton
 * 
 * Uses browser Performance API for accurate timing measurements.
 * Idempotent marks to support React StrictMode (marks are no-op if already set).
 */
class PerformanceTimingServiceImpl {
  private marks: Map<string, number> = new Map();
  private durations: Map<string, PerformanceDuration> = new Map();
  private firstPlaybackMarked = false;

  constructor() {
    Logger.debug('PerformanceTimingService initialized', LOG_TAG);
  }

  /**
   * Record a performance mark
   * Idempotent: if the mark already exists, this is a no-op
   * 
   * @param name - The mark name
   */
  mark(name: string): void {
    // Idempotent: skip if already marked (supports StrictMode double-invocation)
    if (this.marks.has(name)) {
      Logger.debug(`Mark "${name}" already exists, skipping (idempotent)`, LOG_TAG);
      return;
    }

    const timestamp = performance.now();
    this.marks.set(name, timestamp);

    // Also record in browser Performance API for DevTools visibility
    try {
      performance.mark(name);
    } catch {
      // Ignore if mark already exists in browser API
    }

    Logger.debug(`Mark recorded: ${name} at ${timestamp.toFixed(2)}ms`, LOG_TAG);
  }

  /**
   * Record the first playback start mark
   * This is a special mark that should only fire once per session
   * 
   * @returns true if the mark was recorded, false if already marked
   */
  markFirstPlaybackStart(): boolean {
    if (this.firstPlaybackMarked) {
      Logger.debug('First playback already marked, skipping', LOG_TAG);
      return false;
    }

    this.firstPlaybackMarked = true;
    this.mark(PERFORMANCE_MARKS.PLAYBACK_FIRST_START);
    return true;
  }

  /**
   * Measure the duration between two marks
   * 
   * @param name - The measurement name
   * @param startMark - The start mark name
   * @param endMark - The end mark name
   * @returns The duration in milliseconds, or null if marks don't exist
   */
  measure(name: string, startMark: string, endMark: string): number | null {
    const startTime = this.marks.get(startMark);
    const endTime = this.marks.get(endMark);

    if (startTime === undefined || endTime === undefined) {
      Logger.debug(
        `Cannot measure "${name}": missing marks (start: ${startMark}=${startTime}, end: ${endMark}=${endTime})`,
        LOG_TAG
      );
      return null;
    }

    const duration = endTime - startTime;

    const durationRecord: PerformanceDuration = {
      name,
      startMark,
      endMark,
      duration,
    };

    this.durations.set(name, durationRecord);

    // Also record in browser Performance API for DevTools visibility
    try {
      performance.measure(name, startMark, endMark);
    } catch {
      // Ignore measure errors (marks may not exist in browser API)
    }

    Logger.debug(`Measured "${name}": ${duration.toFixed(2)}ms`, LOG_TAG);
    return duration;
  }

  /**
   * Get all recorded marks
   */
  getMarks(): PerformanceMark[] {
    return Array.from(this.marks.entries()).map(([name, timestamp]) => ({
      name,
      timestamp,
    }));
  }

  /**
   * Get all recorded durations
   */
  getDurations(): PerformanceDuration[] {
    return Array.from(this.durations.values());
  }

  /**
   * Get a specific mark timestamp
   */
  getMark(name: string): number | null {
    return this.marks.get(name) ?? null;
  }

  /**
   * Get bootstrap duration (app:bootstrap:start → app:bootstrap:end)
   */
  getBootstrapDuration(): number | null {
    // Try to get from cache first
    const cached = this.durations.get(PERFORMANCE_DURATIONS.BOOTSTRAP);
    if (cached) {
      return cached.duration;
    }

    // Compute and cache
    return this.measure(
      PERFORMANCE_DURATIONS.BOOTSTRAP,
      PERFORMANCE_MARKS.APP_BOOTSTRAP_START,
      PERFORMANCE_MARKS.APP_BOOTSTRAP_END
    );
  }

  /**
   * Get time to playlist ready (app:bootstrap:start → playlist:ready)
   */
  getTimeToPlaylistReady(): number | null {
    const cached = this.durations.get(PERFORMANCE_DURATIONS.TIME_TO_PLAYLIST_READY);
    if (cached) {
      return cached.duration;
    }

    return this.measure(
      PERFORMANCE_DURATIONS.TIME_TO_PLAYLIST_READY,
      PERFORMANCE_MARKS.APP_BOOTSTRAP_START,
      PERFORMANCE_MARKS.PLAYLIST_READY
    );
  }

  /**
   * Get time to ATC stations ready (app:bootstrap:start → atc:stations:ready)
   */
  getTimeToAtcReady(): number | null {
    const cached = this.durations.get(PERFORMANCE_DURATIONS.TIME_TO_ATC_READY);
    if (cached) {
      return cached.duration;
    }

    return this.measure(
      PERFORMANCE_DURATIONS.TIME_TO_ATC_READY,
      PERFORMANCE_MARKS.APP_BOOTSTRAP_START,
      PERFORMANCE_MARKS.ATC_STATIONS_READY
    );
  }

  /**
   * Get time to first playback (app:bootstrap:start → playback:first:start)
   */
  getTimeToFirstPlayback(): number | null {
    const cached = this.durations.get(PERFORMANCE_DURATIONS.TIME_TO_FIRST_PLAYBACK);
    if (cached) {
      return cached.duration;
    }

    return this.measure(
      PERFORMANCE_DURATIONS.TIME_TO_FIRST_PLAYBACK,
      PERFORMANCE_MARKS.APP_BOOTSTRAP_START,
      PERFORMANCE_MARKS.PLAYBACK_FIRST_START
    );
  }

  /**
   * Check if a mark has been recorded
   */
  hasMark(name: string): boolean {
    return this.marks.has(name);
  }

  /**
   * Check if first playback has been marked
   */
  hasFirstPlaybackStarted(): boolean {
    return this.firstPlaybackMarked;
  }

  /**
   * Clear all marks and durations
   * Useful for testing and resetting state
   */
  clear(): void {
    this.marks.clear();
    this.durations.clear();
    this.firstPlaybackMarked = false;

    // Clear browser Performance API entries
    try {
      performance.clearMarks();
      performance.clearMeasures();
    } catch {
      // Ignore if not supported
    }

    Logger.debug('Performance timing data cleared', LOG_TAG);
  }

  /**
   * Get a summary of all timing data for diagnostics
   */
  getSummary(): {
    marks: PerformanceMark[];
    durations: PerformanceDuration[];
    bootstrapDuration: number | null;
    timeToPlaylistReady: number | null;
    timeToAtcReady: number | null;
    timeToFirstPlayback: number | null;
  } {
    return {
      marks: this.getMarks(),
      durations: this.getDurations(),
      bootstrapDuration: this.getBootstrapDuration(),
      timeToPlaylistReady: this.getTimeToPlaylistReady(),
      timeToAtcReady: this.getTimeToAtcReady(),
      timeToFirstPlayback: this.getTimeToFirstPlayback(),
    };
  }

  /**
   * Log a summary of all timing data (useful for diagnostics)
   */
  logSummary(): void {
    const summary = this.getSummary();
    
    Logger.info('Performance Timing Summary', LOG_TAG, {
      markCount: summary.marks.length,
      durationCount: summary.durations.length,
      bootstrapDuration: summary.bootstrapDuration?.toFixed(2) ?? 'N/A',
      timeToPlaylistReady: summary.timeToPlaylistReady?.toFixed(2) ?? 'N/A',
      timeToAtcReady: summary.timeToAtcReady?.toFixed(2) ?? 'N/A',
      timeToFirstPlayback: summary.timeToFirstPlayback?.toFixed(2) ?? 'N/A',
    });
  }
}

// Singleton instance
let instance: PerformanceTimingServiceImpl | null = null;

/**
 * Get the singleton PerformanceTimingService instance
 */
export const getPerformanceTimingService = (): PerformanceTimingServiceImpl => {
  if (!instance) {
    instance = new PerformanceTimingServiceImpl();
  }
  return instance;
};

/**
 * Reset the singleton instance (for testing only)
 */
export const resetPerformanceTimingService = (): void => {
  if (instance) {
    instance.clear();
  }
  instance = null;
};

// Export the service type for typing
export type PerformanceTimingService = PerformanceTimingServiceImpl;

// Default export for convenience
export default getPerformanceTimingService;
